import { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../components/config/api';
import type { UpsellSuggestion } from '../utils/upsell';

const REFRESH_MS = 5 * 60 * 1000;

/**
 * Οι προτάσεις πώλησης που ισχύουν τώρα στο κατάστημα (κεντρικά → «Προτάσεις πώλησης»).
 * Φορτώνονται ΠΡΙΝ από το «Ολοκλήρωση» και ανανεώνονται κάθε 5′ (το ωράριο το κρίνει ο
 * server), ώστε η πρόταση να μην καθυστερεί ποτέ την πληρωμή. Κλειστός διακόπτης, παλιό
 * backend ή σφάλμα δικτύου = καμία πρόταση: το ταμείο δουλεύει όπως πριν.
 */
export function useUpsellSuggestions(storeId: number | undefined, channel: 'take_away' | 'delivery') {
  const [suggestions, setSuggestions] = useState<UpsellSuggestion[]>([]);

  useEffect(() => {
    if (!storeId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/stores/${storeId}/upsell-suggestions?channel=${channel}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('xentrix_token')}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setSuggestions(data?.enabled && Array.isArray(data.suggestions) ? data.suggestions : []);
      } catch {
        /* χωρίς δίκτυο: κρατάμε ό,τι είχαμε */
      }
    };
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [storeId, channel]);

  /** «Εμφανίστηκε» — για την αναφορά των κεντρικών. Ποτέ δεν μπλοκάρει το ταμείο. */
  const recordImpression = useCallback((suggestionId: number) => {
    if (!storeId) return;
    fetch(`${API_BASE_URL}/api/stores/${storeId}/upsell-impressions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('xentrix_token')}`,
      },
      body: JSON.stringify({ suggestion_id: suggestionId, channel }),
    }).catch(() => { /* η καταγραφή δεν είναι κρίσιμη */ });
  }, [storeId, channel]);

  return { suggestions, recordImpression };
}
